/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { produce } from 'immer';
import { ComponentProps } from '../../organisms/HotelInformation/HotelInformation.types';

// Interface for HotelInformation server data state
export interface HotelInformationServerDataState {
  // Core hotel data
  hotelInformationData: ComponentProps['hotelInformationData'];
  propertyId: ComponentProps['propertyId'];
  jsonContent: ComponentProps['jsonContent'];
  amenitiesData: ComponentProps['amenitiesData'];
  accessibilityData: ComponentProps['accessibilityData'];
  generalInformationData: ComponentProps['generalInformationData'];
  brandCode: ComponentProps['brandCode'];

  // Additional props that may be passed
  keyAmenities: ComponentProps['keyAmenities'];
  pageProps: ComponentProps['pageProps'];
  isAuthorMode: ComponentProps['isAuthorMode'];
}

// Interface for store actions
export interface HotelInformationServerDataActions {
  // Initialize the store with server data (following shopStoreInitialization pattern)
  hotelInformationInitialization: (serverData: {
    hotelInformationData?: any;
    propertyId?: string;
    jsonContent?: any;
    amenitiesData?: any;
    accessibilityData?: any;
    generalInformationData?: any;
    brandCode?: string;
    keyAmenities?: any;
    pageProps?: any;
    isAuthorMode?: boolean;
  }) => void;

  // Update individual properties
  updateServerDataProperty: (key: keyof HotelInformationServerDataState, value: any) => void;

  // Bulk update functionality
  updateHotelInformationServerDataState: (updates: Partial<HotelInformationServerDataState>) => void;

  // Reset store to initial state
  resetHotelInformationServerDataStore: () => void;

  // Utility methods for checking state
  isInitialState: () => boolean;
}

// Combined store type
export type HotelInformationServerDataStore = HotelInformationServerDataState & HotelInformationServerDataActions;

// Initial state matching the serverDataProps from HotelInformation component
const initialState: HotelInformationServerDataState = {
  hotelInformationData: {},
  propertyId: '',
  jsonContent: {},
  amenitiesData: {},
  accessibilityData: {},
  generalInformationData: {},
  brandCode: '',
  keyAmenities: undefined,
  pageProps: undefined,
  isAuthorMode: undefined,
};

// Store creator following existing patterns
const hotelInformationServerDataStore: StateCreator<HotelInformationServerDataStore> = (set, get) => {
  return {
    ...initialState,

    // Initialize store with server data (following shopStoreInitialization pattern)
    hotelInformationInitialization: (serverData: {
      hotelInformationData?: any;
      propertyId?: string;
      jsonContent?: any;
      amenitiesData?: any;
      accessibilityData?: any;
      generalInformationData?: any;
      brandCode?: string;
      keyAmenities?: any;
      pageProps?: any;
      isAuthorMode?: boolean;
    }) => {
      set(
        produce(draft => {
          // Initialize with server data (not from context)
          if (serverData.hotelInformationData !== undefined) {
            draft.hotelInformationData = serverData.hotelInformationData;
          }
          if (serverData.propertyId !== undefined) {
            draft.propertyId = serverData.propertyId;
          }
          if (serverData.jsonContent !== undefined) {
            draft.jsonContent = serverData.jsonContent;
          }
          if (serverData.amenitiesData !== undefined) {
            draft.amenitiesData = serverData.amenitiesData;
          }
          if (serverData.accessibilityData !== undefined) {
            draft.accessibilityData = serverData.accessibilityData;
          }
          if (serverData.generalInformationData !== undefined) {
            draft.generalInformationData = serverData.generalInformationData;
          }
          if (serverData.brandCode !== undefined) {
            draft.brandCode = serverData.brandCode;
          }
          if (serverData.keyAmenities !== undefined) {
            draft.keyAmenities = serverData.keyAmenities;
          }
          if (serverData.pageProps !== undefined) {
            draft.pageProps = serverData.pageProps;
          }
          if (serverData.isAuthorMode !== undefined) {
            draft.isAuthorMode = serverData.isAuthorMode;
          }
        })
      );
    },

    // Update individual properties
    updateServerDataProperty: (key: keyof HotelInformationServerDataState, value: any) => {
      set(
        produce(draft => {
          draft[key] = value;
        })
      );
    },

    // Bulk update functionality for multiple properties
    updateHotelInformationServerDataState: (updates: Partial<HotelInformationServerDataState>) => {
      set(
        produce(draft => {
          Object.keys(updates).forEach(key => {
            const typedKey = key as keyof HotelInformationServerDataState;
            draft[typedKey] = updates[typedKey];
          });
        })
      );
    },

    // Reset store to initial state
    resetHotelInformationServerDataStore: () => {
      set({ ...initialState });
    },

    // Utility method to check if store is in initial state
    isInitialState: () => {
      const state = get();
      return (
        Object.keys(state.jsonContent || {}).length === 0 &&
        Object.keys(state.hotelInformationData || {}).length === 0 &&
        Object.keys(state.amenitiesData || {}).length === 0 &&
        Object.keys(state.accessibilityData || {}).length === 0 &&
        Object.keys(state.generalInformationData || {}).length === 0 &&
        !state.propertyId &&
        !state.brandCode
      );
    },
  };
};

// Create the store using the existing createAppStore pattern
export const useHotelInformationServerDataStore = createAppStore(hotelInformationServerDataStore, {
  usePersistentStore: false,
});

// Export selector helpers for common access patterns
export const hotelInformationServerDataSelectors = {
  hotelInformationData: (state: HotelInformationServerDataStore) => state.hotelInformationData,
  propertyId: (state: HotelInformationServerDataStore) => state.propertyId,
  jsonContent: (state: HotelInformationServerDataStore) => state.jsonContent,
  amenitiesData: (state: HotelInformationServerDataStore) => state.amenitiesData,
  accessibilityData: (state: HotelInformationServerDataStore) => state.accessibilityData,
  generalInformationData: (state: HotelInformationServerDataStore) => state.generalInformationData,
  brandCode: (state: HotelInformationServerDataStore) => state.brandCode,
  keyAmenities: (state: HotelInformationServerDataStore) => state.keyAmenities,
  pageProps: (state: HotelInformationServerDataStore) => state.pageProps,
  isAuthorMode: (state: HotelInformationServerDataStore) => state.isAuthorMode,
};
